import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Download, LogOut, Lock, Search, Users, MessageCircle, Send, AlertTriangle, CheckCircle2 } from "lucide-react";
import { ColyAvatar } from "@/components/ColyMascot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "coly_admin_token";

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [draft, setDraft] = useState("");
  const [tab, setTab] = useState("waitlist");
  const [data, setData] = useState(null);
  const [supportData, setSupportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");

  // Broadcast state
  const [broadcastTestEmail, setBroadcastTestEmail] = useState("");
  const [broadcastConfirm, setBroadcastConfirm] = useState(false);
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [broadcastError, setBroadcastError] = useState("");
  const [broadcastMode, setBroadcastMode] = useState("custom"); // "custom" | "beta_testers"
  const [customSubject, setCustomSubject] = useState("");
  const [customHeading, setCustomHeading] = useState("");
  const [customBody, setCustomBody] = useState("");

  const runBroadcast = async ({ test = false, retryFailed = false } = {}) => {
    setBroadcastSending(true);
    setBroadcastResult(null);
    setBroadcastError("");
    try {
      const body = { template: broadcastMode };
      if (broadcastMode === "custom") {
        if (!customSubject.trim() || !customBody.trim()) {
          setBroadcastError("Subject and body are required.");
          setBroadcastSending(false);
          return;
        }
        body.custom_subject = customSubject.trim();
        body.custom_heading = customHeading.trim();
        body.custom_body = customBody;
      }
      if (test) {
        if (!broadcastTestEmail.trim()) {
          setBroadcastError("Enter a test email first.");
          setBroadcastSending(false);
          return;
        }
        body.test_email = broadcastTestEmail.trim();
      }
      if (retryFailed) body.retry_failed = true;
      const res = await axios.post(`${API}/waitlist/admin/broadcast`, body, {
        headers: { "X-Admin-Token": token },
      });
      setBroadcastResult(res.data);
      setBroadcastConfirm(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Broadcast failed.";
      setBroadcastError(typeof msg === "string" ? msg : "Broadcast failed.");
    } finally {
      setBroadcastSending(false);
    }
  };

  const fetchAll = async (t) => {
    setLoading(true);
    setError("");
    try {
      const [w, s] = await Promise.all([
        axios.get(`${API}/waitlist/admin/list`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/support/admin/list`, { headers: { "X-Admin-Token": t } }),
      ]);
      setData(w.data);
      setSupportData(s.data);
      localStorage.setItem(STORAGE_KEY, t);
      setToken(t);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load data.";
      setError(msg);
      if (err?.response?.status === 401) {
        localStorage.removeItem(STORAGE_KEY);
        setToken("");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    const t = setTimeout(() => fetchAll(token), 0);
    return () => clearTimeout(t);
  }, []);

  const onLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setData(null);
    setSupportData(null);
    setDraft("");
  };

  const onDownload = async () => {
    try {
      const res = await axios.get(`${API}/waitlist/admin/export.csv`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "coly_waitlist.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Download failed.");
    }
  };

  const filteredEntries = data?.entries?.filter((e) =>
    e.email.toLowerCase().includes(filter.toLowerCase())
  );

  const filteredSupport = supportData?.entries?.filter((m) => {
    const q = filter.toLowerCase();
    return (
      m.email.toLowerCase().includes(q) ||
      m.name.toLowerCase().includes(q) ||
      m.subject.toLowerCase().includes(q) ||
      m.message.toLowerCase().includes(q)
    );
  });

  if (!token) {
    return (
      <div className="min-h-screen bg-coly-cream flex items-center justify-center px-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.trim()) fetchAll(draft.trim());
          }}
          className="bg-white rounded-3xl border border-coly-ink/10 p-8 max-w-md w-full"
          style={{ boxShadow: "0 30px 80px -40px rgba(47,58,44,0.3)" }}
        >
          <div className="flex justify-center mb-4">
            <ColyAvatar size={56} />
          </div>
          <h1 className="font-black text-[26px] text-center tracking-tight m-0">
            Coly Admin
          </h1>
          <p className="text-coly-ink-soft text-center text-sm mt-1.5 mb-6 font-semibold">
            Enter the admin token to view the dashboard.
          </p>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-coly-ink-soft" />
            <input
              type="password"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Admin token"
              autoFocus
              className="w-full bg-coly-cream border-2 border-coly-ink/10 rounded-full pl-11 pr-4 py-3 outline-none font-semibold text-coly-ink focus:border-coly-green"
            />
          </div>
          {error && (
            <p className="text-red-600 text-sm font-bold mt-3 text-center">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading || !draft.trim()}
            className="mt-5 w-full bg-coly-green text-white rounded-full py-3 font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ boxShadow: "0 4px 0 #4F8E6A" }}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking…
              </>
            ) : (
              "Unlock"
            )}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-coly-cream py-10 px-4 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <header className="flex flex-wrap items-center justify-between gap-3 mb-7">
          <div className="flex items-center gap-3">
            <ColyAvatar size={42} />
            <div>
              <h1 className="font-black text-[22px] sm:text-[26px] tracking-tight m-0">
                Coly Dashboard
              </h1>
              <p className="text-coly-ink-soft text-xs font-bold uppercase tracking-wider">
                {data ? `${data.total} signup${data.total === 1 ? "" : "s"}` : "Loading…"}
                {supportData ? ` · ${supportData.total} message${supportData.total === 1 ? "" : "s"}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {tab === "waitlist" && (
              <button
                onClick={onDownload}
                className="bg-coly-green text-white rounded-full px-4 py-2.5 font-extrabold text-sm inline-flex items-center gap-2"
                style={{ boxShadow: "0 3px 0 #4F8E6A" }}
                data-testid="admin-csv-btn"
              >
                <Download className="w-4 h-4" />
                CSV
              </button>
            )}
            <button
              onClick={onLogout}
              className="bg-white border border-coly-ink/10 text-coly-ink rounded-full px-4 py-2.5 font-extrabold text-sm inline-flex items-center gap-2 hover:bg-coly-cream2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </header>

        <div className="flex gap-2 mb-5">
          <button
            onClick={() => setTab("waitlist")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-extrabold text-sm border transition-colors ${
              tab === "waitlist"
                ? "bg-coly-ink text-white border-coly-ink"
                : "bg-white text-coly-ink border-coly-ink/10 hover:bg-coly-cream2"
            }`}
            data-testid="admin-tab-waitlist"
          >
            <Users className="w-4 h-4" />
            Waitlist
            {data && (
              <span className="ml-1 bg-white/20 text-current rounded-full px-2 text-xs">
                {data.total}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("support")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-extrabold text-sm border transition-colors ${
              tab === "support"
                ? "bg-coly-ink text-white border-coly-ink"
                : "bg-white text-coly-ink border-coly-ink/10 hover:bg-coly-cream2"
            }`}
            data-testid="admin-tab-support"
          >
            <MessageCircle className="w-4 h-4" />
            Support
            {supportData && (
              <span className="ml-1 bg-white/20 text-current rounded-full px-2 text-xs">
                {supportData.total}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("broadcast")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-extrabold text-sm border transition-colors ${
              tab === "broadcast"
                ? "bg-coly-ink text-white border-coly-ink"
                : "bg-white text-coly-ink border-coly-ink/10 hover:bg-coly-cream2"
            }`}
            data-testid="admin-tab-broadcast"
          >
            <Send className="w-4 h-4" />
            Broadcast
          </button>
        </div>

        {tab === "broadcast" ? (
          <BroadcastPanel
            waitlistCount={data?.total ?? 0}
            mode={broadcastMode}
            setMode={setBroadcastMode}
            customSubject={customSubject}
            setCustomSubject={setCustomSubject}
            customHeading={customHeading}
            setCustomHeading={setCustomHeading}
            customBody={customBody}
            setCustomBody={setCustomBody}
            testEmail={broadcastTestEmail}
            setTestEmail={setBroadcastTestEmail}
            confirming={broadcastConfirm}
            setConfirming={setBroadcastConfirm}
            sending={broadcastSending}
            result={broadcastResult}
            errorText={broadcastError}
            onSendTest={() => runBroadcast({ test: true })}
            onSendReal={() => runBroadcast({ test: false })}
            onRetryFailed={() => runBroadcast({ retryFailed: true })}
          />
        ) : (
        <div className="bg-white rounded-3xl border border-coly-ink/10 overflow-hidden">
          <div className="px-5 py-4 border-b border-coly-ink/10 flex items-center gap-2">
            <Search className="w-4 h-4 text-coly-ink-soft" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={tab === "waitlist" ? "Filter by email…" : "Search messages…"}
              className="flex-1 bg-transparent outline-none text-sm font-semibold text-coly-ink placeholder:text-coly-ink-soft/60"
            />
          </div>
          {loading && !data ? (
            <div className="py-20 text-center text-coly-ink-soft">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              <p className="mt-3 font-bold">Loading…</p>
            </div>
          ) : error ? (
            <div className="py-20 text-center text-red-600 font-bold">{error}</div>
          ) : tab === "waitlist" ? (
            filteredEntries && filteredEntries.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-coly-cream2/60 text-coly-ink-soft">
                    <tr>
                      <th className="text-left font-extrabold uppercase tracking-wider text-[10px] px-5 py-3">#</th>
                      <th className="text-left font-extrabold uppercase tracking-wider text-[10px] px-5 py-3">Email</th>
                      <th className="text-left font-extrabold uppercase tracking-wider text-[10px] px-5 py-3 hidden sm:table-cell">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEntries.map((e, idx) => (
                      <tr key={e.id || e.email} className="border-t border-coly-ink/5 hover:bg-coly-cream2/40">
                        <td className="px-5 py-3 font-bold text-coly-ink-soft">{idx + 1}</td>
                        <td className="px-5 py-3 font-bold text-coly-ink break-all">{e.email}</td>
                        <td className="px-5 py-3 text-coly-ink-soft text-xs hidden sm:table-cell">{formatDate(e.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-20 text-center text-coly-ink-soft font-bold">No signups yet.</div>
            )
          ) : filteredSupport && filteredSupport.length > 0 ? (
            <div className="divide-y divide-coly-ink/5" data-testid="admin-support-list">
              {filteredSupport.map((m) => (
                <div key={m.id} className="px-5 sm:px-6 py-5 hover:bg-coly-cream2/40">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-extrabold text-coly-ink text-[15px]">{m.subject}</div>
                      <div className="mt-0.5 text-[13px] font-semibold text-coly-ink-soft break-all">
                        <span className="text-coly-ink">{m.name}</span> · {" "}
                        <a href={`mailto:${m.email}`} className="text-coly-green-deep font-bold underline underline-offset-2">
                          {m.email}
                        </a>
                      </div>
                    </div>
                    <span className="text-[11px] font-bold text-coly-ink-soft whitespace-nowrap">
                      {formatDate(m.timestamp)}
                    </span>
                  </div>
                  <p className="mt-3 text-[14px] text-coly-ink leading-relaxed font-medium whitespace-pre-wrap">
                    {m.message}
                  </p>
                  <div className="mt-3">
                    <a
                      href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`}
                      className="inline-flex items-center gap-1.5 text-[12px] font-extrabold text-coly-green-deep hover:underline"
                    >
                      Reply →
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-20 text-center text-coly-ink-soft font-bold">No support messages yet.</div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

const formatDate = (s) => {
  if (!s) return "";
  try {
    const d = new Date(s);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return s;
  }
};

const BroadcastPanel = ({
  waitlistCount,
  mode,
  setMode,
  customSubject,
  setCustomSubject,
  customHeading,
  setCustomHeading,
  customBody,
  setCustomBody,
  testEmail,
  setTestEmail,
  confirming,
  setConfirming,
  sending,
  result,
  errorText,
  onSendTest,
  onSendReal,
  onRetryFailed,
}) => (
  <div className="bg-white rounded-3xl border border-coly-ink/10 overflow-hidden p-6 sm:p-8" data-testid="broadcast-panel">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-2xl bg-coly-green/15 text-coly-green-deep flex items-center justify-center shrink-0">
        <Send className="w-6 h-6" />
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="font-black text-[22px] tracking-tight m-0">Email Broadcast</h2>
        <p className="text-coly-ink-soft text-sm font-semibold mt-1">
          Send a personally-addressed email to every waitlist subscriber. Wrapped in the branded Coly template.
        </p>
      </div>
    </div>

    {/* Mode switcher */}
    <div className="mt-5 inline-flex bg-coly-cream rounded-full p-1 border border-coly-ink/5" data-testid="broadcast-mode-switch">
      <button
        type="button"
        onClick={() => setMode("custom")}
        className={`px-4 py-2 rounded-full text-sm font-extrabold transition-colors ${
          mode === "custom" ? "bg-coly-ink text-white" : "text-coly-ink-soft hover:text-coly-ink"
        }`}
        data-testid="broadcast-mode-custom"
      >
        Compose
      </button>
      <button
        type="button"
        onClick={() => setMode("beta_testers")}
        className={`px-4 py-2 rounded-full text-sm font-extrabold transition-colors ${
          mode === "beta_testers" ? "bg-coly-ink text-white" : "text-coly-ink-soft hover:text-coly-ink"
        }`}
        data-testid="broadcast-mode-preset"
      >
        Beta tester preset
      </button>
    </div>

    {/* Compose form (custom mode only) */}
    {mode === "custom" && (
      <div className="mt-5 space-y-4">
        <div>
          <label className="block text-[11px] font-extrabold uppercase tracking-wider text-coly-ink-soft mb-1.5">
            Subject (shows in inbox preview)
          </label>
          <input
            type="text"
            value={customSubject}
            onChange={(e) => setCustomSubject(e.target.value)}
            placeholder="e.g. Coly is now on iOS 🎉"
            maxLength={200}
            className="w-full bg-coly-cream border-2 border-coly-ink/10 rounded-xl px-4 py-2.5 outline-none font-semibold text-coly-ink focus:border-coly-green text-sm"
            data-testid="broadcast-subject"
          />
        </div>
        <div>
          <label className="block text-[11px] font-extrabold uppercase tracking-wider text-coly-ink-soft mb-1.5">
            Heading (shown big at top — optional)
          </label>
          <input
            type="text"
            value={customHeading}
            onChange={(e) => setCustomHeading(e.target.value)}
            placeholder="e.g. We&apos;re live on the App Store!"
            maxLength={140}
            className="w-full bg-coly-cream border-2 border-coly-ink/10 rounded-xl px-4 py-2.5 outline-none font-semibold text-coly-ink focus:border-coly-green text-sm"
            data-testid="broadcast-heading"
          />
        </div>
        <div>
          <label className="block text-[11px] font-extrabold uppercase tracking-wider text-coly-ink-soft mb-1.5">
            Body (plain text — blank lines = new paragraphs)
          </label>
          <textarea
            value={customBody}
            onChange={(e) => setCustomBody(e.target.value)}
            placeholder={"Write your message…\n\nBlank line for new paragraph.\nLine break stays as a line break."}
            rows={9}
            className="w-full bg-coly-cream border-2 border-coly-ink/10 rounded-xl px-4 py-2.5 outline-none font-semibold text-coly-ink focus:border-coly-green text-sm resize-y leading-relaxed"
            data-testid="broadcast-body"
          />
          <div className="mt-1 flex items-center justify-between text-[11px] text-coly-ink-soft">
            <span className="font-semibold">
              Will be greeted &quot;Hi {"{firstName}"} 👋&quot; per recipient, then signed &quot;— The Coly team 💚&quot;
            </span>
            <span className="font-bold">{customBody.length} chars</span>
          </div>
        </div>
      </div>
    )}

    {mode === "beta_testers" && (
      <div className="mt-5 bg-coly-cream border border-coly-ink/10 rounded-2xl p-4 text-sm text-coly-ink-soft font-semibold">
        Sends the saved <strong className="text-coly-ink">&quot;Thank you, IBD community&quot;</strong> beta-tester recruitment email
        (20 Android testers · 14 days · WhatsApp group).
      </div>
    )}

    <div className="mt-6 grid sm:grid-cols-2 gap-4">
      {/* Test send */}
      <div className="bg-coly-cream rounded-2xl p-5 border border-coly-ink/5">
        <div className="text-[11px] font-extrabold uppercase tracking-wider text-coly-ink-soft mb-2">
          1. Send a test
        </div>
        <p className="text-sm text-coly-ink-soft font-semibold mb-3">
          Preview the email by sending it to yourself first.
        </p>
        <input
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          placeholder="you@colyapp.com"
          className="w-full bg-white border-2 border-coly-ink/10 rounded-xl px-3 py-2.5 outline-none font-semibold text-coly-ink focus:border-coly-green text-sm"
          data-testid="broadcast-test-email"
        />
        <button
          type="button"
          onClick={onSendTest}
          disabled={sending || !testEmail.trim()}
          className="mt-3 w-full bg-coly-ink text-white rounded-full py-2.5 font-extrabold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="broadcast-test-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send test
        </button>
      </div>

      {/* Real send */}
      <div className="rounded-2xl p-5 border-2 border-coly-green/30" style={{ background: "rgba(111,176,137,0.08)" }}>
        <div className="text-[11px] font-extrabold uppercase tracking-wider text-coly-green-deep mb-2">
          2. Send to entire waitlist
        </div>
        <p className="text-sm text-coly-ink-soft font-semibold mb-3">
          Will email <span className="font-extrabold text-coly-ink">{waitlistCount}</span>{" "}
          subscriber{waitlistCount === 1 ? "" : "s"}. This can&apos;t be undone.
        </p>
        {!confirming ? (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={sending || waitlistCount === 0}
            className="w-full bg-coly-green text-white rounded-full py-2.5 font-extrabold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ boxShadow: "0 3px 0 #4F8E6A" }}
            data-testid="broadcast-real-btn"
          >
            <Send className="w-4 h-4" />
            Send to all {waitlistCount > 0 ? `(${waitlistCount})` : ""}
          </button>
        ) : (
          <div className="space-y-2">
            <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-[12px] text-yellow-900 font-semibold">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                You&apos;re about to email {waitlistCount} people. Are you sure?
              </span>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={sending}
                className="flex-1 bg-white border border-coly-ink/10 text-coly-ink rounded-full py-2.5 font-extrabold text-sm hover:bg-coly-cream2"
                data-testid="broadcast-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSendReal}
                disabled={sending}
                className="flex-1 bg-coly-green text-white rounded-full py-2.5 font-extrabold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ boxShadow: "0 3px 0 #4F8E6A" }}
                data-testid="broadcast-confirm-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Yes, send now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Result */}
    {result && (
      <div
        className="mt-6 bg-coly-green/10 border border-coly-green/30 rounded-2xl p-4"
        data-testid="broadcast-result"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-coly-green-deep shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 text-sm font-semibold text-coly-ink">
            <div className="font-extrabold text-[15px]">
              {result.sent} sent
              {result.failed > 0 && (
                <span className="text-red-700"> · {result.failed} failed</span>
              )}
            </div>
            <div className="text-coly-ink-soft text-[13px] mt-0.5">
              Target: <span className="font-bold">{result.target}</span>
            </div>
          </div>
        </div>

        {result.failed > 0 && (
          <div className="mt-4 bg-white rounded-xl border border-red-200 p-3">
            <div className="text-[11px] font-extrabold uppercase tracking-wider text-red-700 mb-2">
              Failed recipients ({result.failed})
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1 mb-3">
              {(result.failures || []).map((f) => (
                <div key={f.email} className="text-[12px] text-coly-ink">
                  <span className="font-extrabold">{f.email}</span>
                  <span className="text-coly-ink-soft"> — {f.error.slice(0, 140)}</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={onRetryFailed}
              disabled={sending}
              className="bg-coly-ink text-white rounded-full px-4 py-2 font-extrabold text-xs inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="broadcast-retry-btn"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Retry only the failed ones
            </button>
          </div>
        )}
      </div>
    )}
    {errorText && (
      <div className="mt-6 flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl p-4">
        <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
        <div className="text-sm font-semibold text-red-700">{errorText}</div>
      </div>
    )}
  </div>
);

export default Admin;
